import React, { useEffect, useState, useMemo } from "react";
import {
  Edit,
  Trash2,
  Eye,
  Calendar,
  Plus,
  Grid,
  Search,
  Filter,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  List,
  X,
  MoreVertical,
  FileText,
} from "lucide-react";
import {
  Card,
  Button,
  Badge,
  PageHeader,
  EmptyState,
  AdminLayout,
  DeleteConfirmationModal,
  LoadingSpinner,
  ErrorDisplay,
  useToast,
  PaginationControls,
  Modal,
  DateFilter,
  ResetFilterButton,
} from "../components/AdminSharedComponents";
import api from "../../../../utils/api";
import ReportModal from "../report/ReportModal";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

import { FilterContainer } from "../components/ui/FilterContainer";
import { SearchInput } from "../components/ui/SearchInput";
import { DropdownFilter } from "../components/ui/DropdownFilter";
import { DateRangeFilter } from "../components/ui/DateRangeFilter";
import { ResetButton } from "../components/ui/ResetButton";

const Reports = () => {
  const navigate = useNavigate();

  const [userRole, setUserRole] = useState("");

  const isSupervisor = userRole === "supervisor";
  const isAdmin = userRole === "admin";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState({});
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [view, setView] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [dateFilter, setDateFilter] = useState({
    start: null,
    end: null,
  });
  const [showProjectFilter, setShowProjectFilter] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState(null);

  const ITEMS_PER_PAGE = 9;
  const toast = useToast();

  const fetchUserRole = async () => {
    try {
      // Get role from localStorage (same as sidebar approach)
      const user = JSON.parse(localStorage.getItem("user"));
      const role = user?.role || "admin";

      // Alternative: Get from API if needed
      // const response = await api.get('/api/user/profile');
      // const role = response.data.role;

      setUserRole(role);
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole("admin"); // Default fallback
      toast.error("Failed to fetch user permissions");
    }
  };

  useEffect(() => {
    fetchUserRole();
  }, []);

  // Data fetching
  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/api/reports");
      setReports(response.data);

      const uniqueProjectIds = [
        ...new Set(
          response.data
            .filter((report) => report.project_id)
            .map((report) => report.project_id)
        ),
      ];

      await Promise.all(uniqueProjectIds.map(fetchProjectDetails));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError("Gagal memuat laporan. Silakan coba lagi nanti.");
      setLoading(false);
      toast.error("Gagal memuat laporan");
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    await fetchReports();
    setIsRefreshing(false);
    toast.success("Data berhasil diperbarui");
  };

  const fetchProjectDetails = async (projectId) => {
    if (projects[projectId]) return projects[projectId];
    try {
      const response = await api.get(`/api/projects/${projectId}`);
      setProjects((prev) => ({
        ...prev,
        [projectId]: response.data,
      }));
      return response.data;
    } catch (error) {
      console.error(`Error fetching project ${projectId}:`, error);
      return null;
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Handlers
  const handleSearch = debounce((term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, 300);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setIsDeleteModalOpen(true);
    setActiveActionMenu(null);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await api.delete(`/api/reports/${reportToDelete.id}`);
      setReports(reports.filter((report) => report.id !== reportToDelete.id));
      setIsDeleteModalOpen(false);
      toast.success(`Laporan "${reportToDelete.title}" berhasil dihapus`);
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Gagal menghapus laporan. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    if (typeof date === "string") {
      return date;
    }
    return null;
  };

  const resetFilters = () => {
    setFilterStatus("all");
    setSearchTerm("");
    setSelectedProjects([]);
    setDateFilter({ start: null, end: null });
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Computed values
  const filteredReports = useMemo(() => {
    return reports
      .filter((report) => {
        if (
          filterStatus !== "all" &&
          (!report.project_id ||
            projects[report.project_id]?.status !== filterStatus)
        ) {
          return false;
        }

        if (
          searchTerm &&
          !report.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !report.description.toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          return false;
        }

        if (
          selectedProjects.length > 0 &&
          !selectedProjects.includes(report.project_id)
        ) {
          return false;
        }

        const reportDate = new Date(report.created_at);
        if (dateFilter.start && reportDate < new Date(dateFilter.start)) {
          return false;
        }
        if (dateFilter.end && reportDate > new Date(dateFilter.end)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (sortField === "title") {
          return sortDirection === "asc"
            ? a.title.localeCompare(b.title)
            : b.title.localeCompare(a.title);
        }
        return sortDirection === "asc"
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      });
  }, [
    reports,
    filterStatus,
    searchTerm,
    selectedProjects,
    dateFilter,
    sortField,
    sortDirection,
  ]);

  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredReports.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredReports, currentPage]);

  const totalPages = Math.ceil(filteredReports.length / ITEMS_PER_PAGE);

  const getBadgeVariant = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "In Progress":
        return "primary";
      case "On Hold":
        return "warning";
      default:
        return "danger";
    }
  };

  // Mobile Action Menu Component
  const ActionMenu = ({ report, onClose }) => (
    <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32">
      <div className="py-1">
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
          onClick={() => {
            setSelectedReport(report);
            setIsModalOpen(true);
            onClose();
          }}
        >
          <Eye size={14} className="mr-2" />
          View
        </button>
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center"
          onClick={() => {
            window.location.href = `/admin/reports/edit/${report.id}`;
            onClose();
          }}
        >
          <Edit size={14} className="mr-2" />
          Edit
        </button>
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center"
          onClick={() => {
            handleDeleteClick(report);
            onClose();
          }}
        >
          <Trash2 size={14} className="mr-2" />
          Delete
        </button>
      </div>
    </div>
  );

  // Render helpers
  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-3 sm:p-4">
      {paginatedReports.map((report) => (
        <Card
          key={report.id}
          className="hover:shadow-md transition-all relative"
          /* onClick={() => {
            setSelectedReport(report);
            setIsModalOpen(true);
          }} */
          onClick={() => navigate(`/admin/reports/show/${report.id}`)}
        >
          {/* Header with Title and Action Menu */}
          <div className="flex justify-between items-start mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-primary-dark pr-2 flex-1 line-clamp-2">
              {report.title}
            </h3>
            <div className="relative">
              <button
                className="p-1 hover:bg-gray-100 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveActionMenu(
                    activeActionMenu === report.id ? null : report.id
                  );
                }}
              >
                <MoreVertical size={16} className="text-gray-500" />
              </button>
              {activeActionMenu === report.id && (
                <ActionMenu
                  report={report}
                  onClose={() => setActiveActionMenu(null)}
                />
              )}
            </div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {report.project_id && projects[report.project_id] && (
              <Badge variant="primary" className="text-xs">
                {projects[report.project_id].name}
              </Badge>
            )}
            {report.project_id && projects[report.project_id]?.status && (
              <Badge
                variant={getBadgeVariant(projects[report.project_id].status)}
                className="text-xs"
              >
                {projects[report.project_id].status}
              </Badge>
            )}
          </div>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-3 line-clamp-3">
            {report.description}
          </p>

          {/* Date */}
          <div className="text-xs text-gray-500 flex items-center gap-1 mb-4">
            <Calendar size={12} />
            <span>
              {new Date(report.created_at).toLocaleDateString("id-ID", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}
            </span>
          </div>

          {/* Desktop Actions (hidden on mobile) */}
          <div className="hidden sm:flex justify-between items-center">
            {/* <Button
              variant="text"
              className="text-xs !p-0"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedReport(report);
                setIsModalOpen(true);
              }}
            >
              <Eye size={16} className="mr-1" />
              View Details
            </Button> */}
            <Button
              variant="text"
              className="text-xs !p-0"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/reports/show/${report.id}`);
              }}
            >
              <Eye size={16} className="mr-1" />
              View Details
            </Button>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="!px-3 !py-1"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/admin/reports/edit/${report.id}`;
                }}
              >
                <Edit size={16} />
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="!px-3 !py-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(report);
                }}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="overflow-x-auto">
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer"
                onClick={() => handleSort("title")}
              >
                <div className="flex items-center">
                  Title
                  {sortField === "title" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    ))}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Project
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center">
                  Date
                  {sortField === "created_at" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    ))}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedReports.map((report) => (
              <tr
                key={report.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedReport(report);
                  setIsModalOpen(true);
                }}
              >
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-primary-dark truncate">
                    {report.title}
                  </div>
                  <div className="text-xs text-gray-500 line-clamp-1 mt-1">
                    {report.description}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {(report.project_id && projects[report.project_id]?.name) ||
                    "-"}
                </td>
                <td className="px-6 py-4">
                  {report.project_id && projects[report.project_id]?.status ? (
                    <Badge
                      variant={getBadgeVariant(
                        projects[report.project_id].status
                      )}
                    >
                      {projects[report.project_id].status}
                    </Badge>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {new Date(report.created_at).toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `/admin/reports/edit/${report.id}`;
                      }}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(report);
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List View */}
      <div className="md:hidden p-3">
        <div className="space-y-3">
          {paginatedReports.map((report) => (
            <Card
              key={report.id}
              className="hover:shadow-md transition-all"
              onClick={() => {
                setSelectedReport(report);
                setIsModalOpen(true);
              }}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-sm font-semibold text-primary-dark pr-2 flex-1 line-clamp-2">
                  {report.title}
                </h3>
                <div className="relative">
                  <button
                    className="p-1 hover:bg-gray-100 rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenu(
                        activeActionMenu === report.id ? null : report.id
                      );
                    }}
                  >
                    <MoreVertical size={16} className="text-gray-500" />
                  </button>
                  {activeActionMenu === report.id && (
                    <ActionMenu
                      report={report}
                      onClose={() => setActiveActionMenu(null)}
                    />
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-2">
                {report.project_id && projects[report.project_id] && (
                  <Badge variant="primary" className="text-xs">
                    {projects[report.project_id].name}
                  </Badge>
                )}
                {report.project_id && projects[report.project_id]?.status && (
                  <Badge
                    variant={getBadgeVariant(
                      projects[report.project_id].status
                    )}
                    className="text-xs"
                  >
                    {projects[report.project_id].status}
                  </Badge>
                )}
              </div>

              <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                {report.description}
              </p>

              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Calendar size={10} />
                <span>
                  {new Date(report.created_at).toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );

  const renderProjectFilterModal = () => (
    <Modal
      isOpen={showProjectFilter}
      onClose={() => setShowProjectFilter(false)}
      title="Filter Proyek"
    >
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Pilih Proyek</h3>
          <Button
            variant="text"
            onClick={() => setSelectedProjects([])}
            className="text-sm"
          >
            Reset
          </Button>
        </div>

        <div className="max-h-60 overflow-y-auto border rounded p-2">
          {Object.values(projects).length > 0 ? (
            Object.values(projects).map((project) => (
              <div
                key={project.id}
                className="flex items-center p-2 hover:bg-gray-50 rounded"
              >
                <input
                  type="checkbox"
                  id={`project-${project.id}`}
                  checked={selectedProjects.includes(project.id)}
                  onChange={() => {
                    setSelectedProjects((prev) =>
                      prev.includes(project.id)
                        ? prev.filter((id) => id !== project.id)
                        : [...prev, project.id]
                    );
                  }}
                  className="mr-2 h-4 w-4"
                />
                <label
                  htmlFor={`project-${project.id}`}
                  className="flex-1 cursor-pointer text-sm"
                >
                  {project.name}
                </label>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              Tidak ada proyek tersedia
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
        <Button variant="secondary" onClick={() => setShowProjectFilter(false)}>
          Batal
        </Button>
        <Button variant="primary" onClick={() => setShowProjectFilter(false)}>
          Terapkan Filter
        </Button>
      </div>
    </Modal>
  );

  const renderMobileFilters = () => (
    <Modal
      isOpen={showMobileFilters}
      onClose={() => setShowMobileFilters(false)}
      title="Filter & Urutkan"
    >
      <div className="space-y-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          >
            <option value="all">Semua Status</option>
            <option value="In Progress">Dalam Proses</option>
            <option value="Completed">Selesai</option>
            <option value="On Hold">Ditahan</option>
          </select>
        </div>

        {/* Project Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Proyek
          </label>
          <Button
            variant="secondary"
            className="w-full justify-start"
            onClick={() => {
              setShowMobileFilters(false);
              setShowProjectFilter(true);
            }}
          >
            <Filter size={16} className="mr-2" />
            Filter Proyek
            {selectedProjects.length > 0 && (
              <span className="ml-auto bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                {selectedProjects.length}
              </span>
            )}
          </Button>
        </div>

        {/* Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tanggal
          </label>
          {/*           <DateFilter
            selectedDate={dateFilter}
            onDateChange={(newDate) => setDateFilter(newDate)}
            className="w-full"
          /> */}
          <DateFilter
            selectedDate={dateFilter}
            onDateChange={(newDate) => {
              setDateFilter({
                start: formatDate(newDate.start),
                end: formatDate(newDate.end),
              });
            }}
            className="w-full"
          />
        </div>
        {/* Sort Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Urutkan Berdasarkan
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="sort-date"
                name="sort"
                checked={sortField === "created_at"}
                onChange={() => setSortField("created_at")}
                className="mr-2"
              />
              <label htmlFor="sort-date" className="text-sm">
                Tanggal
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="sort-title"
                name="sort"
                checked={sortField === "title"}
                onChange={() => setSortField("title")}
                className="mr-2"
              />
              <label htmlFor="sort-title" className="text-sm">
                Judul
              </label>
            </div>
          </div>
        </div>

        {/* Sort Direction */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Urutan
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="sort-asc"
                name="direction"
                checked={sortDirection === "asc"}
                onChange={() => setSortDirection("asc")}
                className="mr-2"
              />
              <label htmlFor="sort-asc" className="text-sm">
                Naik (A-Z, Lama-Baru)
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="sort-desc"
                name="direction"
                checked={sortDirection === "desc"}
                onChange={() => setSortDirection("desc")}
                className="mr-2"
              />
              <label htmlFor="sort-desc" className="text-sm">
                Turun (Z-A, Baru-Lama)
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6 pt-4 border-t">
        <Button variant="secondary" onClick={resetFilters} className="flex-1">
          Reset Filter
        </Button>
        <Button
          variant="primary"
          onClick={() => setShowMobileFilters(false)}
          className="flex-1"
        >
          Terapkan
        </Button>
      </div>
    </Modal>
  );

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveActionMenu(null);
    };

    if (activeActionMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeActionMenu]);

  return (
    <AdminLayout>
      <PageHeader
        title="Reports"
        subtitle={
          isSupervisor ? "View all projects" : "Manage and view all projects"
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={refreshData}
              disabled={isRefreshing}
              className="flex items-center"
            >
              <RefreshCw
                size={16}
                className={`mr-1 ${isRefreshing ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="primary"
              onClick={() => (window.location.href = "/admin/reports/add")}
              className="flex items-center"
            >
              <Plus size={16} className="mr-1" />
              <span className="hidden sm:inline">Tambah Laporan</span>
              <span className="sm:hidden">Tambah</span>
            </Button>
          </div>
        }
      />

      <FilterContainer>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search reports..."
        />
        <DropdownFilter
          label="Status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: "all", label: "Semua Status" },
            { value: "In Progress", label: "Dalam Proses" },
            { value: "Completed", label: "Selesai" },
            { value: "On Hold", label: "Ditahan" },
          ]}
        />
        <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
        <ResetButton onClick={resetFilters} />
      </FilterContainer>

      <Card className="mt-6">
        {/* Search and Filters */}
        <div className="p-3 sm:p-4 border-b border-gray-100">
          {/* Search Bar */}
          <div className="flex items-center w-full relative mb-3">
            <Search size={16} className="absolute left-3 text-gray-400" />
            <input
              type="text"
              placeholder="Cari laporan..."
              className="pl-10 pr-10 py-2 border border-gray-200 rounded-lg w-full text-sm"
              onChange={(e) => handleSearch(e.target.value)}
              value={searchTerm}
            />
            {searchTerm && (
              <button
                className="absolute right-3 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setSearchTerm("");
                  handleSearch("");
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Desktop Filters */}
          <div className="hidden md:flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 h-10"
              >
                <option value="all">Semua Status</option>
                <option value="In Progress">Dalam Proses</option>
                <option value="Completed">Selesai</option>
                <option value="On Hold">Ditahan</option>
              </select>

              <Button
                variant="secondary"
                className="h-10"
                onClick={() => setShowProjectFilter(true)}
              >
                <Filter size={16} className="mr-1" />
                Filter Proyek
                {selectedProjects.length > 0 && (
                  <span className="ml-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {selectedProjects.length}
                  </span>
                )}
              </Button>

              <DateFilter
                selectedDate={{
                  start: formatDate(dateFilter.start),
                  end: formatDate(dateFilter.end),
                }}
                onDateChange={(newDate) => setDateFilter(newDate)}
              />

              <ResetFilterButton onReset={resetFilters} />
            </div>

            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden h-10">
              <Button
                variant={view === "grid" ? "primary" : "secondary"}
                className="rounded-none h-full px-3"
                onClick={() => setView("grid")}
              >
                <Grid size={16} />
              </Button>
              <Button
                variant={view === "list" ? "primary" : "secondary"}
                className="rounded-none h-full px-3"
                onClick={() => setView("list")}
              >
                <List size={16} />
              </Button>
            </div>
          </div>

          {/* Mobile Filters */}
          <div className="md:hidden flex justify-between items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowMobileFilters(true)}
              className="flex-1 justify-center"
            >
              <Filter size={16} className="mr-1" />
              Filter
              {(filterStatus !== "all" ||
                selectedProjects.length > 0 ||
                dateFilter.start ||
                dateFilter.end) && (
                <span className="ml-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  !
                </span>
              )}
            </Button>

            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <Button
                variant={view === "grid" ? "primary" : "secondary"}
                className="rounded-none px-3 py-2"
                onClick={() => setView("grid")}
              >
                <Grid size={16} />
              </Button>
              <Button
                variant={view === "list" ? "primary" : "secondary"}
                className="rounded-none px-3 py-2"
                onClick={() => setView("list")}
              >
                <List size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-8">
            <LoadingSpinner message="Memuat laporan..." />
          </div>
        ) : error ? (
          <ErrorDisplay error={error} />
        ) : paginatedReports.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={FileText}
              message={
                reports.length === 0
                  ? "No reports available"
                  : "No reports found"
              }
              {...(reports.length === 0
                ? {
                    buttonLabel: "Create First Report",
                    buttonLink: "/admin/reports/add",
                  }
                : filterStatus !== "all" ||
                  searchTerm ||
                  selectedProjects.length > 0 ||
                  dateFilter.start ||
                  dateFilter.end
                ? {
                    buttonLabel: "Reset Filters",
                    buttonOnClick: resetFilters,
                  }
                : {})}
            />
          </div>
        ) : (
          <>
            {view === "grid" ? renderGridView() : renderListView()}

            <div className="p-3 sm:p-4 border-t border-gray-100">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                onNext={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </Card>

      {/* Modals */}
      {renderProjectFilterModal()}
      {renderMobileFilters()}

      <ReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        report={selectedReport}
        project={
          selectedReport?.project_id
            ? projects[selectedReport.project_id]
            : null
        }
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
        itemToDelete={reportToDelete}
      />
    </AdminLayout>
  );
};

export default Reports;
